import { Card, CardContent } from '@/components/ui/card';
import { useTimeSummary } from '@/hooks/use-time-entries';
import { cn } from '@/lib/utils';
import { Clock, TrendingUp, FolderKanban } from 'lucide-react';

interface TimeSummaryProps {
  from: string;
  to: string;
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

export function TimeSummary({ from, to }: TimeSummaryProps) {
  // The exact week, summed by the server on UTC days. Until the backfill
  // converts legacy rows, a legacy night row counts here in its UTC day while
  // the Weekly Timesheet shows it on the viewer's day, so near a week's edge
  // the two can disagree; the runbook runs the backfill right after this
  // frontend deploys, which closes the gap. [HW-916]
  const { data: summary } = useTimeSummary(from, to);

  const totalFormatted = summary ? formatDuration(summary.totalSeconds) : '0h';
  const dailyAvg = summary ? formatDuration(summary.dailyAvgSeconds) : '0h';
  const targetWeekly = 40 * 3600; // 40h target
  const progressPct = summary ? Math.min(100, Math.round((summary.totalSeconds / targetWeekly) * 100)) : 0;

  const progressColor =
    progressPct >= 90 ? 'from-green-500 to-emerald-400' :
    progressPct >= 50 ? 'from-hygge to-teal-400' :
    'from-amber-500 to-orange-400';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {/* Weekly Total */}
      <Card className="transition-all hover:border-hygge/20">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Weekly Total</span>
            <div className="h-7 w-7 rounded-lg bg-hygge/10 flex items-center justify-center">
              <Clock className="h-3.5 w-3.5 text-hygge" />
            </div>
          </div>
          <p className="text-2xl font-semibold text-foreground tracking-tight">{totalFormatted}</p>
          <div className="mt-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">{progressPct}% of 40h</span>
              <span className="text-[10px] font-medium text-foreground">{totalFormatted} / 40h</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full bg-gradient-to-r transition-all duration-500 ease-out',
                  progressColor,
                )}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Daily Average */}
      <Card className="transition-all hover:border-hygge/20">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Daily Average</span>
            <div className="h-7 w-7 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <TrendingUp className="h-3.5 w-3.5 text-blue-500" />
            </div>
          </div>
          <p className="text-2xl font-semibold text-foreground tracking-tight">{dailyAvg}</p>
          <p className="text-[11px] text-muted-foreground mt-1.5">
            {summary?.workingDays ?? 0} working day{(summary?.workingDays ?? 0) !== 1 ? 's' : ''}
          </p>
        </CardContent>
      </Card>

      {/* By Project */}
      <Card className="transition-all hover:border-hygge/20">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">By Project</span>
            <div className="h-7 w-7 rounded-lg bg-violet-500/10 flex items-center justify-center">
              <FolderKanban className="h-3.5 w-3.5 text-violet-500" />
            </div>
          </div>
          <div className="space-y-2">
            {summary?.byProject.slice(0, 4).map((p) => {
              const pctOfTotal = summary.totalSeconds > 0
                ? Math.round((p.totalSeconds / summary.totalSeconds) * 100)
                : 0;
              return (
                <div key={p.projectId} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: p.projectColor }} />
                      <span className="text-xs text-foreground truncate max-w-[120px]">{p.projectName}</span>
                    </div>
                    <span className="text-[11px] text-muted-foreground font-mono">{formatDuration(p.totalSeconds)}</span>
                  </div>
                  <div className="h-1 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${pctOfTotal}%`, backgroundColor: p.projectColor }}
                    />
                  </div>
                </div>
              );
            }) ?? (
              <p className="text-xs text-muted-foreground">No time tracked yet</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
